import { useState, useEffect, useCallback } from 'react';
import { eventService, handleEventApiError, transformEventData } from '../services/eventService';

export const useEventManagement = (initialFilters = {}) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    category: '',
    eventType: '',
    pricingType: '',
    ...initialFilters
  });
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    active: 0,
    completed: 0
  });

  // Fetch events
  const fetchEvents = useCallback(async (page = 1, newFilters = null) => {
    setLoading(true);
    setError('');
    
    try {
      const currentFilters = newFilters || filters;
      const params = {
        page,
        limit: pagination.limit,
        ...currentFilters
      };

      const response = await eventService.getAllEvents(params);
      
      if (response.success) {
        const transformedEvents = transformEventData(response.data);
        setEvents(transformedEvents);
        setPagination({
          page: response.pagination?.currentPage || page,
          limit: response.pagination?.itemsPerPage || pagination.limit,
          total: response.pagination?.totalItems || 0,
          totalPages: response.pagination?.totalPages || 0
        });
      } else {
        setError(response.message || 'Failed to fetch events');
      }
    } catch (err) {
      const errorMessage = handleEventApiError(err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.limit]);

  // Fetch pending events
  const fetchPendingEvents = useCallback(async (page = 1, newFilters = null) => {
    setLoading(true);
    setError('');
    
    try {
      const currentFilters = newFilters || filters;
      const params = {
        page,
        limit: pagination.limit,
        ...currentFilters
      };

      const response = await eventService.getPendingEvents(params);
      
      if (response.success) {
        const transformedEvents = transformEventData(response.data);
        setEvents(transformedEvents);
        setPagination({
          page: response.pagination?.currentPage || page,
          limit: response.pagination?.itemsPerPage || pagination.limit,
          total: response.pagination?.totalItems || 0,
          totalPages: response.pagination?.totalPages || 0
        });
      } else {
        setError(response.message || 'Failed to fetch pending events');
      }
    } catch (err) {
      const errorMessage = handleEventApiError(err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.limit]);

  // Fetch event statistics
  const fetchStats = useCallback(async () => {
    try {
      const response = await eventService.getEventStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch event stats:', err);
    }
  }, []);

  // Approve event
  const approveEvent = useCallback(async (eventId, approvalData = {}) => {
    try {
      const response = await eventService.approveEvent(eventId, approvalData);
      if (response.success) {
        // Update the event in the local state
        setEvents(prevEvents => 
          prevEvents.map(event => 
            event.id === eventId 
              ? { ...event, status: 'approved' }
              : event
          )
        );
        // Refresh stats
        fetchStats();
        return { success: true, message: response.message };
      } else {
        return { success: false, message: response.message };
      }
    } catch (err) {
      const errorMessage = handleEventApiError(err);
      return { success: false, message: errorMessage };
    }
  }, [fetchStats]);

  // Reject event
  const rejectEvent = useCallback(async (eventId, rejectionData = {}) => {
    try {
      const response = await eventService.rejectEvent(eventId, rejectionData);
      if (response.success) {
        // Update the event in the local state
        setEvents(prevEvents => 
          prevEvents.map(event => 
            event.id === eventId 
              ? { ...event, status: 'rejected' }
              : event
          )
        );
        // Refresh stats
        fetchStats();
        return { success: true, message: response.message };
      } else {
        return { success: false, message: response.message };
      }
    } catch (err) {
      const errorMessage = handleEventApiError(err);
      return { success: false, message: errorMessage };
    }
  }, [fetchStats]);

  // Update event status
  const updateEventStatus = useCallback(async (eventId, status, reason = '') => {
    try {
      const response = await eventService.updateEventStatus(eventId, status, reason);
      if (response.success) {
        // Update the event in the local state
        setEvents(prevEvents => 
          prevEvents.map(event => 
            event.id === eventId 
              ? { ...event, status }
              : event
          )
        );
        // Refresh stats
        fetchStats();
        return { success: true, message: response.message };
      } else {
        return { success: false, message: response.message };
      }
    } catch (err) {
      const errorMessage = handleEventApiError(err);
      return { success: false, message: errorMessage };
    }
  }, [fetchStats]);

  // Toggle featured status
  const toggleFeatured = useCallback(async (eventId, isFeatured) => {
    try {
      const response = await eventService.toggleFeatured(eventId, isFeatured);
      if (response.success) {
        // Update the event in the local state
        setEvents(prevEvents => 
          prevEvents.map(event => 
            event.id === eventId 
              ? { ...event, isFeatured: response.data.isFeatured }
              : event
          )
        );
        return { success: true, message: response.message };
      } else {
        return { success: false, message: response.message };
      }
    } catch (err) {
      const errorMessage = handleEventApiError(err);
      return { success: false, message: errorMessage };
    }
  }, []);

  // Delete event
  const deleteEvent = useCallback(async (eventId) => {
    try {
      const response = await eventService.deleteEvent(eventId);
      if (response.success) {
        // Remove the event from local state
        setEvents(prevEvents => prevEvents.filter(event => event.id !== eventId));
        // Refresh stats
        fetchStats();
        return { success: true, message: response.message };
      } else {
        return { success: false, message: response.message };
      }
    } catch (err) {
      const errorMessage = handleEventApiError(err);
      return { success: false, message: errorMessage };
    }
  }, [fetchStats]);

  // Change page
  const changePage = useCallback((newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
    fetchEvents(newPage);
  }, [fetchEvents]);

  // Update filters
  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setPagination(prev => ({ ...prev, page: 1 }));
  }, []);

  // Clear filters
  const clearFilters = useCallback(() => {
    setFilters({
      search: '',
      status: '',
      category: '',
      eventType: '',
      pricingType: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  }, []);

  // Reset to first page
  const resetToFirstPage = useCallback(() => {
    changePage(1);
  }, [changePage]);

  // Clear error
  const clearError = useCallback(() => {
    setError('');
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchEvents(1);
    fetchStats();
  }, []);

  // Fetch events when filters change
  useEffect(() => {
    fetchEvents(1);
  }, [filters]);

  return {
    // State
    events,
    loading,
    error,
    pagination,
    filters,
    stats,
    
    // Actions
    fetchEvents,
    fetchPendingEvents,
    fetchStats,
    approveEvent,
    rejectEvent,
    updateEventStatus,
    toggleFeatured,
    deleteEvent,
    changePage,
    updateFilters,
    clearFilters,
    resetToFirstPage,
    clearError
  };
}; 